/**
 * Configuration arrives at runtime, never baked into the bundle.
 *
 * One artefact is promoted from Development to Production byte for byte, so anything that
 * differs between Environments has to live beside the bundle rather than inside it. The deploy
 * writes `dist/config.json` from that Environment's own variables; locally there is no deploy,
 * so `.env.local` stands in.
 *
 * This is the shape the deploy actually writes. Changing a key here means changing
 * `.github/workflows/deploy.yml` in the same commit, or the app loads and cannot sign in --
 * which shows up as a blank page, not as a failed deploy.
 */
export type AppConfig = {
  readonly entraClientId: string
  readonly entraAuthority: string
  readonly entraAuthorityHost: string
  readonly apiScope: string
  readonly apiBase: string
}

const KEYS = [
  'entraClientId',
  'entraAuthority',
  'entraAuthorityHost',
  'apiScope',
  'apiBase',
] as const

export class ConfigError extends Error {}

/** Narrows whatever was fetched or read from the environment, naming every key that is missing. */
export function parseConfig(source: unknown, from: string): AppConfig {
  if (typeof source !== 'object' || source === null) {
    throw new ConfigError(`${from} did not provide an object.`)
  }
  const record = source as Record<string, unknown>
  const missing = KEYS.filter((k) => typeof record[k] !== 'string' || record[k] === '')
  if (missing.length > 0) {
    throw new ConfigError(`${from} is missing: ${missing.join(', ')}.`)
  }
  return Object.fromEntries(KEYS.map((k) => [k, record[k]])) as unknown as AppConfig
}

/** The local fallback: the VITE_ names in .env.local, mapped to the deploy's key names. */
export function configFromEnv(env: Record<string, unknown>): AppConfig {
  return parseConfig(
    {
      entraClientId: env['VITE_ENTRA_CLIENT_ID'],
      entraAuthority: env['VITE_ENTRA_AUTHORITY'],
      entraAuthorityHost: env['VITE_ENTRA_AUTHORITY_HOST'],
      apiScope: env['VITE_API_SCOPE'],
      apiBase: env['VITE_API_BASE'],
    },
    '.env.local',
  )
}

/**
 * config.json first, .env.local second. A deployed app has the file and must not fall back:
 * falling back there would sign users into whatever the bundle happened to carry.
 */
export async function loadConfig(
  fetchImpl: typeof fetch = fetch,
  env: Record<string, unknown> = import.meta.env,
): Promise<AppConfig> {
  let response: Response
  try {
    response = await fetchImpl('/config.json', { cache: 'no-store' })
  } catch {
    return configFromEnv(env)
  }
  if (response.status === 404) return configFromEnv(env)
  if (!response.ok) {
    throw new ConfigError(`config.json responded ${response.status}.`)
  }
  // A 404 is not the only way for the file to be absent. Both the dev server and a Static Web
  // App answer an unknown path with index.html and a 200, so absence usually arrives as HTML.
  // Parsing it would throw a SyntaxError about an unexpected '<' instead of falling back.
  const type = response.headers.get('content-type') ?? ''
  if (!type.includes('json')) return configFromEnv(env)
  return parseConfig(await response.json(), 'config.json')
}
